/**
 * Visualization registry: first-party visualizations plus custom ones (imported ISF shaders),
 * shared by the renderer and the UI.
 */
import type { VizLookup } from '../../../shared/scene';
import { VISUALIZATIONS, type VisualizationDef } from '../visualizations';
import { NativeViz, type ProgramCache, type VizInstance } from './instances';

export class VizRegistry {
  private custom = new Map<string, VisualizationDef>();
  private factories = new Map<string, (def: VisualizationDef) => VizInstance>();

  setCustom(defs: VisualizationDef[]): void {
    this.custom = new Map(defs.map((d) => [d.id, d]));
  }

  /** Register an instance factory for a visualization kind (ISF, Butterchurn…). */
  registerKind(kind: string, factory: (def: VisualizationDef) => VizInstance): void {
    this.factories.set(kind, factory);
  }

  get(id: string): VisualizationDef | undefined {
    return VISUALIZATIONS.find((v) => v.id === id) ?? this.custom.get(id);
  }

  /** Like get(), but falls back to the first built-in so a missing visualization still draws. */
  resolve(id: string): VisualizationDef {
    return this.get(id) ?? VISUALIZATIONS[0]!;
  }

  all(): VisualizationDef[] {
    return [...VISUALIZATIONS, ...this.custom.values()];
  }

  readonly lookup: VizLookup = (id) => this.get(id);

  create(def: VisualizationDef, cache: ProgramCache): VizInstance {
    const kind = def.kind ?? 'native';
    const factory = this.factories.get(kind);
    return factory ? factory(def) : new NativeViz(cache, def);
  }
}
